/**
 * FEAT-107 / S-435 — one banner per social account that needs attention,
 * with one action for the whole group of posts the pause caught.
 *
 * An Admin or Editor fixes the cause at the provider, then picks Fixed —
 * retry these, or Discard these. A Reviewer sees the banner without buttons
 * (FEAT-108: never show a control a role cannot use).
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApi, useApiQuery } from "@/lib/api";
import { useRole } from "@/lib/useRole";
import { attentionSummary, discardConfirm, type AccountAttention } from "@/lib/account-attention";
import { describeSocialActionError } from "@/lib/social-action-errors";

export function AccountAttentionBanner() {
  const { canEdit } = useRole();
  const api = useApi();
  const qc = useQueryClient();
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { data } = useApiQuery<{ data: AccountAttention[] }>(["social-account-attention"], "/social-account-attention", {
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const accounts = data?.data ?? [];
  if (accounts.length === 0) return null;

  async function act(a: AccountAttention, action: "resume" | "discard") {
    if (action === "discard" && !window.confirm(discardConfirm(a))) return;
    setBusy(a.account_id);
    setError(null);
    try {
      await api(`/social-account-attention/${a.account_id}/${action}`, { method: "POST" });
      void qc.invalidateQueries({ queryKey: ["social-account-attention"] });
      void qc.invalidateQueries({ queryKey: ["social-calendar"] });
    } catch (err) {
      setError(describeSocialActionError(err, action === "resume" ? "Retry failed." : "Discard failed."));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={{ marginBottom: 12 }}>
      {accounts.map((a) => (
        <div key={a.account_id} className="settings-form-error" role="alert" style={{ marginTop: 8 }}>
          <div>
            <strong>{attentionSummary(a)}</strong>
          </div>
          {a.reason && <div style={{ marginTop: 4 }}>{a.reason}</div>}
          {canEdit && !a.is_owner && (
            <div style={{ marginTop: 8 }}>
              This account is shared with this workspace; the workspace that owns it marks it fixed.
            </div>
          )}
          {canEdit && a.is_owner && (
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button
                type="button"
                className="btn-primary"
                disabled={busy === a.account_id}
                onClick={() => void act(a, "resume")}
                title="You fixed it at the provider: send these posts again."
              >
                {busy === a.account_id ? "Working…" : "Fixed — retry these"}
              </button>
              <button
                type="button"
                className="btn-ghost btn-danger"
                disabled={busy === a.account_id}
                onClick={() => void act(a, "discard")}
                title="Delete these posts. Posts scheduled further ahead are kept."
              >
                Discard these
              </button>
            </div>
          )}
        </div>
      ))}
      {error && <div className="settings-form-error" style={{ marginTop: 8 }}>{error}</div>}
    </div>
  );
}
